import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { useAuth } from './auth.context';
import { PlayerMatch, PlayerProfile, SimilarityCriteria, SimilarPlayer } from '../types/player-similarity';

interface PlayerSimilarityContextType {
  playerName: string;
  setPlayerName: (name: string) => void;
  playerMatches: PlayerMatch[];
  selectedPlayer: PlayerProfile | null;
  isSearchingName: boolean;
  isLoading: boolean;
  error: string | null;
  hasSearched: boolean;
  similarityCriteria: SimilarityCriteria;
  setSimilarityCriteria: (criteria: SimilarityCriteria) => void;
  totalWeight: number;
  isCriteriaValid: boolean;
  similarPlayers: SimilarPlayer[];
  isSearchingSimilar: boolean;
  formatAgeRange: (age: number, range: number) => string;
  formatHeightRange: (height: number, range: number) => string;
  formatMarketValueRange: (value: number, range: number) => string;
  formatMinutesPlayedRange: (minutes: number, rangePercent: number) => string;
  handleNameSearch: () => Promise<void>;
  handlePlayerSelection: (playerId: string, playerName: string) => Promise<void>;
  performSimilaritySearch: () => Promise<void>;
  clearSearch: (options?: { preserveSelectedPlayer?: boolean }) => void;
}

const PlayerSimilarityContext = createContext<PlayerSimilarityContextType | undefined>(undefined);

export const PlayerSimilarityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [playerName, setPlayerName] = useState<string>('');
  const [playerMatches, setPlayerMatches] = useState<PlayerMatch[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerProfile | null>(null);
  const [isSearchingName, setIsSearchingName] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [similarPlayers, setSimilarPlayers] = useState<SimilarPlayer[]>([]);
  const [isSearchingSimilar, setIsSearchingSimilar] = useState<boolean>(false);
  const [similarityCriteria, setSimilarityCriteria] = useState<SimilarityCriteria>({
    position: 20,
    age: {
      weight: 15,
      range: 2
    },
    height: {
      weight: 10,
      range: 5
    },
    marketValue: {
      weight: 15,
      range: 100000
    },
    goals: 20,
    assists: 10,
    minutesPlayed: {
      weight: 10,
      range: 10
    },
    nationality: 0,
    foot: 0
  });
  
  const { getToken } = useAuth();
  
  // Calculate total weight and validity
  const totalWeight = useMemo(() => {
    return Object.entries(similarityCriteria).reduce((sum, [key, value]) => {
      if (typeof value === 'number') {
        return sum + value;
      } else if (typeof value === 'object' && 'weight' in value) {
        return sum + value.weight;
      }
      return sum;
    }, 0);
  }, [similarityCriteria]);

  const isCriteriaValid = totalWeight === 100;

  // Helper functions for range formatting
  const formatAgeRange = (age: number, range: number) => {
    return `${Math.max(age - range, 16)}-${age + range}`;
  };

  const formatHeightRange = (height: number, range: number) => {
    return `${Math.max(height - range, 150)}-${height + range}`;
  };

  const formatMarketValueRange = (value: number, range: number) => {
    const min = Math.max(value - range, 0);
    const max = value + range;
    return `€${min.toLocaleString()}-€${max.toLocaleString()}`;
  };

  const formatMinutesPlayedRange = (minutes: number, rangePercent: number) => {
    const rangeValue = minutes * (rangePercent / 100);
    const min = Math.max(minutes - rangeValue, 0);
    const max = minutes + rangeValue;
    return `${Math.round(min)}-${Math.round(max)}`;
  };

  const handleNameSearch = async () => {
    if (!playerName.trim()) {
      setError('Please enter a player name');
      return;
    }

    setIsSearchingName(true);
    setError(null);
    setHasSearched(true);
    setPlayerMatches([]);
    setSelectedPlayer(null);

    try {
      const token = getToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      // Use the Python API endpoint for player search
      const response = await fetch(`${baseUrl}/ai/v1/players/ids-for-name?name=${encodeURIComponent(playerName)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && Array.isArray(data)) {
        setPlayerMatches(data.map((player: any) => ({
          id: player.id,
          name: player.name
        })));

        // If only one player is found, select it automatically
        if (data.length === 1) {
          await handlePlayerSelection(data[0].id, data[0].name);
        }
      } else {
        setPlayerMatches([]);
      }
    } catch (error) {
      console.error('Error searching for player:', error);
      setError(error instanceof Error ? error.message : 'Failed to search for player');
    } finally {
      setIsSearchingName(false);
    }
  };

  const handlePlayerSelection = async (playerId: string, playerName: string) => {
    setIsLoading(true);
    setError(null);
    
    // Clear any previous similar players results
    setSimilarPlayers([]);

    try {
      const token = getToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      // Use the Python API endpoint for player profile
      const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const playerData = await response.json();
      
      // Ensure minutes_played is properly set if it exists
      if (playerData.minutes_played && typeof playerData.minutes_played === 'string') {
        playerData.minutes_played = parseInt(playerData.minutes_played, 10);
      }
      
      // Set player name if it was passed
      if (playerName) {
        setPlayerName(playerName);
      }
      
      setSelectedPlayer(playerData);
    } catch (error) {
      console.error('Error fetching player details:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch player details');
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = (options?: { preserveSelectedPlayer?: boolean }) => {
    setPlayerName('');
    setPlayerMatches([]);
    if (!options?.preserveSelectedPlayer) {
      setSelectedPlayer(null);
    }
    setIsSearchingName(false);
    setIsLoading(false);
    setError(null);
    setHasSearched(false);
    setSimilarPlayers([]);
  };

  const performSimilaritySearch = async () => {
    if (!selectedPlayer || !isCriteriaValid) {
      setError('Please select a player and ensure criteria weights total 100%');
      return;
    }

    setIsSearchingSimilar(true);
    setError(null);
    setSimilarPlayers([]);

    try {
      const token = getToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      // Prepare the request payload
      const payload = {
        playerId: selectedPlayer.id,
        criteria: {
          position: similarityCriteria.position,
          age: {
            weight: similarityCriteria.age.weight,
            range: similarityCriteria.age.range
          },
          height: {
            weight: similarityCriteria.height.weight,
            range: similarityCriteria.height.range
          },
          marketValue: {
            weight: similarityCriteria.marketValue.weight,
            range: similarityCriteria.marketValue.range
          },
          goals: similarityCriteria.goals,
          assists: similarityCriteria.assists,
          minutesPlayed: {
            weight: similarityCriteria.minutesPlayed.weight,
            range: similarityCriteria.minutesPlayed.range
          },
          nationality: similarityCriteria.nationality,
          foot: similarityCriteria.foot
        }
      };

      console.log('Sending similarity search request:', payload);

      // Call the similarity search API
      const response = await fetch(`${baseUrl}/api/v1/players/similarity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
      }

      const data = await response.json();
      console.log('Similarity search response:', data);
      
      if (data && data.similarPlayers) {
        setSimilarPlayers(data.similarPlayers);
      } else {
        setSimilarPlayers([]);
        setError('No similar players found');
      }
    } catch (error) {
      console.error('Error searching for similar players:', error);
      setError(error instanceof Error ? error.message : 'Failed to search for similar players');
    } finally {
      setIsSearchingSimilar(false);
    }
  };

  return (
    <PlayerSimilarityContext.Provider
      value={{
        playerName,
        setPlayerName,
        playerMatches,
        selectedPlayer,
        isSearchingName,
        isLoading,
        error,
        hasSearched,
        similarityCriteria,
        setSimilarityCriteria,
        totalWeight,
        isCriteriaValid,
        similarPlayers,
        isSearchingSimilar,
        formatAgeRange,
        formatHeightRange,
        formatMarketValueRange,
        formatMinutesPlayedRange,
        handleNameSearch,
        handlePlayerSelection,
        performSimilaritySearch,
        clearSearch
      }}
    >
      {children}
    </PlayerSimilarityContext.Provider>
  );
};

export const usePlayerSimilarity = () => {
  const context = useContext(PlayerSimilarityContext);
  if (context === undefined) {
    throw new Error('usePlayerSimilarity must be used within a PlayerSimilarityProvider');
  }
  return context;
};